import type { NateraIntakePacketInput } from "@medbillai/generated-graphql-types";

import type { FlowState, ScreenKey } from "../../lib/flow-engine";
import { sentryService } from "../../lib/sentry";
import { nateraIntakeStorage } from "../../lib/storage/instances";
import type {
  NateraIntakeAnswers,
  NateraIntakeConfig,
} from "./NateraIntakeFlow";
import { buildNateraIntakePacket } from "./packet";

const STATE_KEY = "flowState";
const OAUTH_PENDING_KEY = "oauthPending";

/**
 * Bump whenever a screen's `needs` change shape. A persisted session from before the
 * change would restore a screen missing data it now requires, so old sessions are
 * dropped rather than half-restored.
 */
const SESSION_VERSION = 15;

/**
 * How long a half-finished set of answers is worth resuming. Long enough to cover a
 * refresh and the hop out to the member's inbox and back; short enough that answers
 * given about one bill don't reappear weeks later on a shared browser. The
 * cross-browser hop doesn't rely on this copy — the packet stored at send does.
 */
const SESSION_TTL_MS = 48 * 60 * 60 * 1000;

const SCREEN_TAGS = {
  NateraConfirm: true,
  Stress: true,
  Feeling: true,
  PersonaValidation: true,
  Playbook: true,
  BillCount: true,
  BillAmount: true,
  Household: true,
  Income: true,
  Analyzing: true,
  FaResult: true,
  Story: true,
  SocialProof: true,
  Account: true,
  NotNateraBill: true,
  ExpressBillCount: true,
  ExpressBillAmount: true,
  ExpressSocialProof: true,
  ExpressAccount: true,
} satisfies Record<ScreenKey<NateraIntakeConfig>, true>;

const isScreenTag = (tag: string): boolean => Object.hasOwn(SCREEN_TAGS, tag);

type PersistedSession = {
  version: number;
  /** Epoch milliseconds; the record is dropped rather than resumed once past. */
  expiresAt: number;
  state: FlowState<NateraIntakeConfig>;
  /**
   * Every answer given, which is wider than the screen's own state: going back
   * restores a screen from before it was answered, so the answers are tracked
   * separately to keep a revisited question populated.
   */
  answers: NateraIntakeAnswers;
  singularStarted?: boolean;
};

export type RestoredIntakeSession = {
  state: FlowState<NateraIntakeConfig>;
  answers: NateraIntakeAnswers;
  singularStarted: boolean;
};

const isPersistedSession = (value: unknown): value is PersistedSession => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as {
    version?: unknown;
    expiresAt?: unknown;
    state?: unknown;
  };
  if (
    candidate.version !== SESSION_VERSION ||
    typeof candidate.expiresAt !== "number"
  ) {
    return false;
  }
  const state = candidate.state;
  if (typeof state !== "object" || state === null) {
    return false;
  }
  const tag = (state as { tag?: unknown }).tag;
  return typeof tag === "string" && isScreenTag(tag);
};

export const saveIntakeSession = async (
  state: FlowState<NateraIntakeConfig>,
  answers: NateraIntakeAnswers,
  singularStarted = false,
): Promise<void> => {
  const session: PersistedSession = {
    version: SESSION_VERSION,
    expiresAt: Date.now() + SESSION_TTL_MS,
    state,
    answers,
    singularStarted,
  };
  await nateraIntakeStorage.set(STATE_KEY, JSON.stringify(session));
};

export const clearIntakeSession = async (): Promise<void> => {
  await nateraIntakeStorage.remove(STATE_KEY);
  await nateraIntakeStorage.remove(OAUTH_PENDING_KEY);
};

/**
 * Returns the persisted screen, or null when there's nothing usable to resume. A
 * malformed or expired record, or a storage that won't be read at all, is dropped
 * rather than thrown: a member mid-funnel should restart the questions, never hit
 * an error page.
 */
export const loadIntakeSession =
  async (): Promise<RestoredIntakeSession | null> => {
    try {
      const raw = await nateraIntakeStorage.get(STATE_KEY);
      if (!raw) {
        return null;
      }
      const parsed: unknown = JSON.parse(raw);
      if (!isPersistedSession(parsed) || parsed.expiresAt <= Date.now()) {
        await clearIntakeSession();
        return null;
      }
      return {
        state: parsed.state,
        answers: parsed.answers ?? {},
        singularStarted: parsed.singularStarted === true,
      };
    } catch (error) {
      sentryService.captureException(error);
      await clearIntakeSession();
      return null;
    }
  };

const OAUTH_PENDING_TTL_MS = 30 * 60 * 1000;

export const markOAuthPending = async (): Promise<void> => {
  await nateraIntakeStorage.set(
    OAUTH_PENDING_KEY,
    String(Date.now() + OAUTH_PENDING_TTL_MS),
  );
};

export const clearOAuthPending = async (): Promise<void> => {
  await nateraIntakeStorage.remove(OAUTH_PENDING_KEY);
};

export const readOAuthPacket = async (): Promise<
  NateraIntakePacketInput | undefined
> => {
  const expiresAt = Number(await nateraIntakeStorage.get(OAUTH_PENDING_KEY));
  if (!(expiresAt > Date.now())) {
    return undefined;
  }
  const restored = await loadIntakeSession();
  if (
    restored?.state.tag !== "Account" &&
    restored?.state.tag !== "ExpressAccount"
  ) {
    return undefined;
  }
  return buildNateraIntakePacket({
    ...restored.answers,
    ...restored.state.data,
  });
};
